import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { useCurrency } from '@/Hooks/useCurrency';
import { ChartTip, KpiTile, MeterRow, Panel } from './components';
import { FloorHeatmap } from './FloorHeatmap';
import { QueryBuilder } from './QueryBuilder';
import { changeRatio, previousRange, seriesStats, shareOf } from './revenue';
import {
  RANGE_PRESETS,
  axisStyle,
  grainFor,
  presetRange,
  rangeToIso,
  screenCurrency,
  seriesColors,
  useAnalyticsMeta,
  useAnalyticsQuery,
  useBranchOptions,
  useFormat,
  type KpiResult,
  type SemanticRow,
} from './analytics';

/**
 * The summary export's columns. Named here because a *fixed* export is a fixed
 * report, but every name is checked against `/analytics/meta` before the
 * request goes out (see `exportMeasures`) — a measure retired server-side then
 * disappears from the file instead of turning the download into a 400.
 */
const SUMMARY_EXPORT_MEASURES = [
  'revenue',
  'orders',
  'items',
  'averageTicket',
  'discountTotal',
  'refundTotal',
];

/**
 * P13-06 - the reporting screen.
 *
 * Every panel is a selection against the §32 semantic layer rather than its own
 * endpoint, so the date range and branch filter apply to all of them by being
 * part of the same query object. Adding "revenue by waiter" here is a new query
 * literal, not a new API call to build.
 *
 * Frame `64:2647` ("Revenue Report") is the top of this page: a headline row,
 * a daily trend beside a ranked category breakdown, and a top-items table. What
 * the frame drops is kept below it — the operational §15.2 trio, the commercial
 * measures, the fulfillment split, resource utilisation, the kitchen SLA table,
 * the floor heatmap and the `QueryBuilder`. Deleting the builder would delete
 * every report that is not one of the four drawn.
 *
 * Four of the frame's figures are not measures and cannot be queried — see
 * `revenue.ts`. Three are derived from the series already on screen; the
 * period-over-period captions cost a **second** `/analytics/kpis` call, because
 * `AnalyticsQueryDto` has no `compareTo`.
 */
export default function ReportsDashboard() {
  const { t, i18n } = useTranslation();
  // Last resort only: every branch carries a currency, and useCurrency resolves
  // branch › tenant before this is ever reached.
  const { code: fallbackCurrency } = useCurrency();
  const { can } = usePermission();

  const [range, setRange] = useState(() => presetRange(6));
  const [branchId, setBranchId] = useState('');
  const [exporting, setExporting] = useState(false);

  const { branches } = useBranchOptions();
  // C-11 — the semantic registry, fetched once and shared with the builder.
  const meta = useAnalyticsMeta();
  // D-02 — "All branches" over a chain that trades in more than one currency has
  // no single currency to label the sum with. `screenCurrency` says so (null)
  // rather than borrowing `branches[0].currency`, and the notice below explains
  // why the money columns lose their symbol until a branch is picked.
  const { code: currency, mixed, codes } = screenCurrency(branches, branchId, fallbackCurrency);
  const format = useFormat(currency);

  const iso = useMemo(() => rangeToIso(range.from, range.to), [range]);
  const filters = useMemo(() => (branchId ? { branch: branchId } : undefined), [branchId]);
  const grain = grainFor(iso.from, iso.to);

  /**
   * `+12%` needs a number to compare against and the API cannot supply one in
   * the same request, so the previous window of equal length is a second call.
   * Deliberately not "last month": there is no `month` bucket to align to.
   */
  const before = useMemo(() => previousRange(iso), [iso]);

  // Headline figures, the dine-in/takeaway split and every §15.2 interval.
  const kpis = useApi<KpiResult>('/analytics/kpis', {
    from: iso.from,
    to: iso.to,
    ...(branchId ? { branchId } : {}),
  });

  const previousKpis = useApi<KpiResult>('/analytics/kpis', {
    from: before.from,
    to: before.to,
    ...(branchId ? { branchId } : {}),
  });

  const trend = useAnalyticsQuery({
    measures: ['revenue'],
    dimensions: [grain],
    filters,
    dateRange: iso,
    limit: 200,
  });

  const categories = useAnalyticsQuery({
    measures: ['revenue'],
    dimensions: ['category'],
    filters,
    dateRange: iso,
    limit: 12,
  });

  // Two dimensions, under the DTO's `@ArrayMaxSize(4)`. An item belongs to one
  // category, so grouping by both names the category without splitting a row.
  const topItems = useAnalyticsQuery({
    measures: ['revenue', 'items', 'grossMargin'],
    dimensions: ['catalogItem', 'category'],
    filters,
    dateRange: iso,
    limit: 10,
  });

  const kitchen = useAnalyticsQuery({
    measures: ['items', 'prepSeconds', 'pickupDelaySeconds', 'orderToTableSeconds'],
    dimensions: ['station'],
    filters,
    dateRange: iso,
    limit: 20,
  });

  const resources = useAnalyticsQuery({
    measures: ['resourceUtilisation', 'resourceSeconds'],
    dimensions: ['resource'],
    filters,
    dateRange: iso,
    limit: 20,
  });

  const commercial = kpis.data?.commercial ?? {};
  const operational = kpis.data?.operational ?? {};
  const fulfillment = kpis.data?.fulfillment ?? [];
  const fulfillmentTotal = fulfillment.reduce((sum, row) => sum + Number(row.revenue ?? 0), 0);
  const previousCommercial = previousKpis.data?.commercial ?? {};

  const trendStats = useMemo(
    () => seriesStats(trend.data?.rows ?? [], 'revenue', grain),
    [trend.data, grain],
  );

  const categoryRows = categories.data?.rows ?? [];
  const categoryTotal = categoryRows.reduce((sum, row) => sum + Number(row.revenue ?? 0), 0);

  /** `+12%` / `−4%`, in the reader's locale. `format.percent` has no sign rule. */
  const signedPercent = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language || 'en', {
        style: 'percent',
        maximumFractionDigits: 1,
        signDisplay: 'exceptZero',
      }),
    [i18n.language],
  );

  const versusPrevious = (current: unknown, previous: unknown): string => {
    const ratio = changeRatio(
      (current ?? null) as never,
      (previous ?? null) as never,
    );
    if (ratio === null) {
      return t('reports.kpi.noBaseline');
    }
    return t('reports.kpi.vsPrevious', {
      change: signedPercent.format(ratio),
    });
  };

  // Only the columns the server still publishes. Until meta lands this is
  // empty, which is what disables the button — better than a download that 400s.
  const exportMeasures = useMemo(() => {
    const published = new Set((meta.data?.measures ?? []).map((measure) => measure.name));
    return SUMMARY_EXPORT_MEASURES.filter((name) => published.has(name));
  }, [meta.data]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.post(
        '/analytics/query/export',
        {
          measures: exportMeasures,
          dimensions: ['date'],
          filters,
          dateRange: iso,
          limit: meta.data?.limits.maxRows ?? 1000,
        },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `venue-report-${range.from}-${range.to}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('reporting.tabs.dashboard')}</h2>
        {can('reports.export') && (
          <Button
            size="sm"
            onClick={exportCsv}
            disabled={exporting || !exportMeasures.length}
          >
            <Icon as={Download} aria-hidden />
            {t('reports.export')}
          </Button>
        )}
      </div>

      {/* Filters sit in one row above every panel; they are the whole query. */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-surface p-4">
        <div className="flex flex-wrap gap-2">
          {RANGE_PRESETS.map((preset) => {
            const value = presetRange(preset.days);
            const active = value.from === range.from && value.to === range.to;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => setRange(value)}
                className={
                  active
                    ? 'rounded-lg bg-accent-solid px-3 py-1.5 text-sm text-accent-on'
                    : 'rounded-lg border px-3 py-1.5 text-sm'
                }
              >
                {t(`reports.ranges.${preset.key}`, { defaultValue: preset.key })}
              </button>
            );
          })}
        </div>

        <div className="w-36">
          <Field label={t('reports.filters.from')}>
            <Input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(event) =>
                setRange((current) => ({ ...current, from: event.target.value }))
              }
            />
          </Field>
        </div>
        <div className="w-36">
          <Field label={t('reports.filters.to')}>
            <Input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(event) => setRange((current) => ({ ...current, to: event.target.value }))}
            />
          </Field>
        </div>
        <div className="w-56">
          <Field label={t('nav.branches')}>
            <Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
              <option value="">{t('reports.filters.allBranches')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {mixed && (
        <p
          role="status"
          className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {t('reports.currency.mixedNotice', { codes: codes.join(', ') })}
        </p>
      )}

      {/* Frame `64:2647`'s headline row. Two of the four are derivations and
          two carry a comparison the API cannot answer in one request. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label={t('reports.kpi.revenue')}
          value={format.money(commercial.revenue ?? null)}
          hint={versusPrevious(commercial.revenue, previousCommercial.revenue)}
        />
        <KpiTile
          label={
            grain === 'hour'
              ? t('reports.kpi.avgHourly')
              : t('reports.kpi.avgDaily')
          }
          value={format.money(trendStats.average)}
          hint={t('reports.kpi.avgDailyHint', {
            count: trendStats.buckets,
          })}
          muted={trendStats.average === null}
        />
        <KpiTile
          label={
            grain === 'hour'
              ? t('reports.kpi.peakHour')
              : t('reports.kpi.peakDay')
          }
          value={
            trendStats.peak
              ? grain === 'hour'
                ? format.hour(trendStats.peak.key)
                : format.day(trendStats.peak.key)
              : format.money(null)
          }
          hint={trendStats.peak ? format.money(trendStats.peak.value) : undefined}
          muted={!trendStats.peak}
        />
        <KpiTile
          label={t('reports.kpi.ordersCount')}
          value={format.number(commercial.orders ?? null)}
          hint={versusPrevious(commercial.orders, previousCommercial.orders)}
        />
      </div>

      {/* P13-04 commercial headline — not drawn on the frame, and not deleted. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiTile
          label={t('reports.kpi.averageTicket')}
          value={format.money(commercial.averageTicket ?? null)}
          hint={t('reports.kpi.guests', { count: Number(commercial.guests ?? 0) })}
        />
        <KpiTile
          label={t('reports.kpi.grossMargin')}
          value={format.money(commercial.grossMargin ?? null)}
          hint={`${t('reports.kpi.cogs')} ${format.money(commercial.cogs ?? null)}`}
        />
        <KpiTile
          label={t('reports.kpi.tableTurnover')}
          value={format.number(commercial.tableTurnover ?? null, 2)}
          hint={`${format.number(commercial.sessions ?? null)} ${t('reports.kpi.sessions')}`}
        />
      </div>

      {/* P13-03 operational headline - §15.2 keeps these three apart on purpose */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label={t('reports.kpi.prep')}
          value={format.duration(operational.prepSeconds ?? null)}
          hint={t('reports.kpi.prepHint')}
          muted={operational.prepSeconds === null}
        />
        <KpiTile
          label={t('reports.kpi.pickup')}
          value={format.duration(operational.pickupDelaySeconds ?? null)}
          hint={t('reports.kpi.pickupHint')}
          muted={operational.pickupDelaySeconds === null}
        />
        <KpiTile
          label={t('reports.kpi.orderToTable')}
          value={format.duration(operational.orderToTableSeconds ?? null)}
          hint={t('reports.kpi.orderToTableHint')}
          muted={operational.orderToTableSeconds === null}
        />
        <KpiTile
          label={t('reports.kpi.waiterResponse')}
          value={format.duration(operational.waiterResponseSeconds ?? null)}
          hint={t('reports.kpi.waiterResponseHint')}
          muted={operational.waiterResponseSeconds === null}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel
            title={t('reports.trend.title')}
            description={
              grain === 'hour' ? t('reports.trend.byHour') : t('reports.trend.byDay')
            }
            loading={trend.loading}
            failed={trend.failed}
            empty={!trend.loading && !trend.data?.rows.length}
            onRetry={trend.reload}
          >
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={(trend.data?.rows ?? []).map((row) => ({
                    ...row,
                    revenueValue: Number(row.revenue ?? 0),
                  }))}
                  margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
                >
                  <defs>
                    <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={seriesColors[0]} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={seriesColors[0]} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={axisStyle.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey={grain}
                    tick={axisStyle.tick}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) =>
                      grain === 'hour' ? format.hour(value as number) : format.day(value as string)
                    }
                  />
                  <YAxis
                    tick={axisStyle.tick}
                    tickLine={false}
                    axisLine={false}
                    width={72}
                    tickFormatter={(value) => format.number(value as number)}
                  />
                  <Tooltip
                    cursor={{ stroke: axisStyle.grid }}
                    content={
                      <ChartTip
                        formatValue={(_key, value) => format.money(value ?? null)}
                        formatLabel={(label) =>
                          grain === 'hour'
                            ? format.hour(label as number)
                            : format.day(label as string)
                        }
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="revenueValue"
                    name={t('reports.kpi.revenue')}
                    stroke={seriesColors[0]}
                    strokeWidth={2}
                    fill="url(#revenueFill)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* A short ranked list reads better as bars beside their labels than as
            a chart — `MeterRow`'s stated reason for existing, and exactly the
            shape the frame draws. */}
        <Panel
          title={t('reports.category.title')}
          description={t('reports.topItems.description')}
          loading={categories.loading}
          failed={categories.failed}
          empty={!categories.loading && !categoryRows.length}
          onRetry={categories.reload}
        >
          <div className="space-y-4">
            {categoryRows.map((row, index) => (
              <MeterRow
                key={String(row.category ?? index)}
                label={String(row.categoryLabel ?? row.category ?? '—')}
                value={format.money(row.revenue ?? null)}
                share={shareOf(row.revenue ?? null, categoryTotal) ?? 0}
                color={seriesColors[index % seriesColors.length]}
              />
            ))}
          </div>
        </Panel>
      </div>

      <Panel
        title={t('reports.topItems.title')}
        description={t('reports.topItems.description')}
        loading={topItems.loading}
        failed={topItems.failed}
        empty={!topItems.loading && !topItems.data?.rows.length}
        onRetry={topItems.reload}
      >
        <TableWrap className="border-0">
          <Table>
            <THead>
              <TR>
                <TH>{t('reports.topItems.rank')}</TH>
                <TH>{t('reports.topItems.item')}</TH>
                <TH>{t('reports.dimensions.category')}</TH>
                <TH className="text-end">{t('common.quantity')}</TH>
                <TH className="text-end">{t('reports.kpi.revenue')}</TH>
                <TH className="text-end">
                  {t('reports.topItems.share')}
                </TH>
                <TH className="text-end">{t('reports.kpi.grossMargin')}</TH>
              </TR>
            </THead>
            <tbody>
              {(topItems.data?.rows ?? []).map((row: SemanticRow, index) => {
                // Measured against the window's own revenue, which is what the
                // frame's sample figures divide by. Null — not 0% — when that
                // total cannot carry a share at all.
                const share = shareOf(row.revenue ?? null, Number(commercial.revenue ?? 0));
                return (
                  <TR key={`${String(row.catalogItem)}:${String(row.category)}`}>
                    <TD className="tabular-nums text-ink-muted">{index + 1}</TD>
                    <TD>{String(row.catalogItemLabel ?? row.catalogItem ?? '')}</TD>
                    <TD className="text-ink-muted">
                      {String(row.categoryLabel ?? row.category ?? '—')}
                    </TD>
                    <TD className="text-end tabular-nums">{format.number(row.items ?? null, 2)}</TD>
                    <TD className="text-end tabular-nums">{format.money(row.revenue ?? null)}</TD>
                    <TD className="text-end tabular-nums">
                      {share === null ? '—' : format.percent(share)}
                    </TD>
                    <TD className="text-end tabular-nums">
                      {format.money(row.grossMargin ?? null)}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel
          title={t('reports.fulfillment.title')}
          description={t('reports.fulfillment.description')}
          loading={kpis.loading}
          failed={!!kpis.error}
          empty={!kpis.loading && !fulfillment.length}
          onRetry={kpis.reload}
        >
          <div className="space-y-4">
            {fulfillment.map((row, index) => (
              <MeterRow
                key={String(row.fulfillmentType)}
                label={t(`order.fulfillment.${row.fulfillmentType}`, String(row.fulfillmentType))}
                value={format.money(row.revenue ?? null)}
                share={shareOf(row.revenue ?? null, fulfillmentTotal) ?? 0}
                color={seriesColors[index % seriesColors.length]}
              />
            ))}
          </div>
        </Panel>

        <Panel
          title={t('reports.resources.title')}
          description={t('reports.resources.description')}
          loading={resources.loading}
          failed={resources.failed}
          empty={!resources.loading && !resources.data?.rows.length}
          onRetry={resources.reload}
        >
          <div className="space-y-4">
            {(resources.data?.rows ?? []).map((row) => (
              <MeterRow
                key={String(row.resource)}
                label={String(row.resourceLabel ?? row.resource ?? '')}
                value={`${format.percent(row.resourceUtilisation ?? null)} · ${format.duration(row.resourceSeconds ?? null)}`}
                share={Number(row.resourceUtilisation ?? 0)}
                color={seriesColors[0]}
              />
            ))}
          </div>
        </Panel>
      </div>

      <Panel
        title={t('reports.kitchen.title')}
        description={t('reports.kitchen.description')}
        loading={kitchen.loading}
        failed={kitchen.failed}
        empty={!kitchen.loading && !kitchen.data?.rows.length}
        onRetry={kitchen.reload}
      >
        <TableWrap className="border-0">
          <Table>
            <THead>
              <TR>
                <TH>{t('reports.kitchen.station')}</TH>
                <TH className="text-end">{t('reports.kpi.prep')}</TH>
                <TH className="text-end">{t('reports.kpi.pickup')}</TH>
                <TH className="text-end">{t('reports.kpi.orderToTable')}</TH>
              </TR>
            </THead>
            <tbody>
              {(kitchen.data?.rows ?? []).map((row: SemanticRow) => (
                <TR key={String(row.station)}>
                  <TD>
                    <span className="block">{String(row.stationLabel ?? row.station ?? '')}</span>
                    <span className="text-xs text-ink-muted">
                      {t('kds.board.itemCount', { count: Number(row.items ?? 0) })}
                    </span>
                  </TD>
                  <TD className="text-end tabular-nums">
                    {format.duration(row.prepSeconds ?? null)}
                  </TD>
                  <TD className="text-end tabular-nums">
                    {format.duration(row.pickupDelaySeconds ?? null)}
                  </TD>
                  <TD className="text-end tabular-nums">
                    {format.duration(row.orderToTableSeconds ?? null)}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Panel>

      <FloorHeatmap
        branchId={branchId || undefined}
        from={iso.from}
        to={iso.to}
        currency={currency}
      />

      {/* The panels above are the fixed reports; this is everything else the
          semantic layer can answer, offered straight from its own registry. */}
      <QueryBuilder
        meta={meta}
        filters={filters}
        dateRange={iso}
        currency={currency}
        canExport={can('reports.export')}
        fileStem={`venue-report-${range.from}-${range.to}`}
      />
    </div>
  );
}
